// Lead roster for Smith Luxury Group, Miami. All 18 leads are demo data
// intended to feel like a real brokerage's live pipeline.
//
// Temperatures: hot, warm, nurture, cold, dead, red_flag
// Statuses:     new, qualifying, qualified, verified, showing,
//               nurture, closed_won, closed_lost, disqualified, dead
//
// Agent IDs reference /frontend/src/data/agents.js.
// Property IDs reference /frontend/src/data/properties.js.
// Project IDs reference /frontend/src/data/projects.js.

import {
  DEMO_BRIEF_VOLKOV,
  DEMO_BRIEF_JAMES_CHEN,
  DEMO_BRIEF_DAVID_RODRIGUEZ,
  DEMO_BRIEF_ROBERTO_SILVA,
  DEMO_BRIEF_SARAH_WILLIAMS,
} from './demoBriefs.js';

// Base "now" used across all seed files so relative times stay consistent.
// Anchored to the moment the app loads so the dashboard always looks live.
export const NOW_ISO = new Date().toISOString();

const NOW_MS = Date.parse(NOW_ISO);
export const minutesAgo = (m) => new Date(NOW_MS - m * 60_000).toISOString();
export const hoursAgo = (h) => new Date(NOW_MS - h * 3_600_000).toISOString();
export const daysAgo = (d) => new Date(NOW_MS - d * 86_400_000).toISOString();
export const daysAhead = (d) => new Date(NOW_MS + d * 86_400_000).toISOString();
export const hoursAhead = (h) => new Date(NOW_MS + h * 3_600_000).toISOString();

export const leads = [
  // ── HOT ───────────────────────────────────────────────────────────────────
  {
    id: 'lead-sofia-martinez',
    name: 'Sofia Martinez',
    email: 'sofia.martinez@demo.example',
    phone: '+1-305-555-1201',
    preferredLanguage: 'es',
    source: 'website',
    temperature: 'hot',
    status: 'verified',
    score: 91,
    confidenceLabel: 'high',
    assignedAgentId: 'agent-natalia-ortiz',
    budget: { min: 4_500_000, max: 6_500_000, currency: 'USD' },
    timeline: '30-60 days',
    neighborhoods: ['Coral Gables', 'Coconut Grove'],
    propertyType: 'single_family',
    bedroomsMin: 5,
    financingType: 'cash',
    occupation: 'Cardiologist · Baptist Health',
    notes: 'Selling Pinecrest home, needs 30-day close, cash buyer verified.',
    interestedPropertyIds: ['prop-05', 'prop-07'],
    interestedProjectIds: [],
    tags: ['cash', 'local', 'move-up'],
    firstContactAt: hoursAgo(22),
    lastContactAt: hoursAgo(3),
    createdAt: hoursAgo(22),
    updatedAt: hoursAgo(3),
  },
  {
    id: 'lead-james-chen',
    name: 'James Chen',
    email: 'james.chen@demo.example',
    phone: '+1-415-555-1202',
    preferredLanguage: 'en',
    source: 'referral',
    temperature: 'hot',
    status: 'qualified',
    score: 88,
    confidenceLabel: 'high',
    assignedAgentId: 'agent-ana-castillo',
    budget: { min: 6_000_000, max: 9_500_000, currency: 'USD' },
    timeline: '60-90 days',
    neighborhoods: ['Brickell', 'Downtown Miami'],
    propertyType: 'condo',
    bedroomsMin: 3,
    financingType: 'mortgage_80',
    occupation: 'VP Engineering · public fintech',
    notes: 'Relocating from SF, pre-approved with JPMorgan, interested in pre-con + ready units.',
    interestedPropertyIds: ['prop-02', 'prop-09', 'prop-10'],
    interestedProjectIds: ['proj-mandarin-oriental', 'proj-st-regis'],
    tags: ['relo', 'tech', 'financing'],
    demoBrief: DEMO_BRIEF_JAMES_CHEN,
    firstContactAt: hoursAgo(14),
    lastContactAt: hoursAgo(4),
    createdAt: hoursAgo(14),
    updatedAt: hoursAgo(4),
  },
  {
    id: 'lead-patricia-dubois',
    name: 'Patricia Dubois',
    email: 'patricia.dubois@demo.example',
    phone: '+33-1-55-55-1203',
    preferredLanguage: 'en',
    source: 'zillow',
    temperature: 'hot',
    status: 'qualified',
    score: 92,
    confidenceLabel: 'high',
    assignedAgentId: 'agent-marco-rivera',
    budget: { min: 12_000_000, max: 20_000_000, currency: 'USD' },
    timeline: '90 days',
    neighborhoods: ['Miami Beach', 'Key Biscayne', 'Golden Beach'],
    propertyType: 'single_family',
    bedroomsMin: 6,
    financingType: 'cash',
    occupation: 'Founder · Paris luxury goods',
    notes: 'Second home. Will fly in for three-day tour. Needs waterfront with dockage.',
    interestedPropertyIds: ['prop-03', 'prop-06', 'prop-01'],
    interestedProjectIds: [],
    tags: ['intl', 'cash', 'ultra-luxury'],
    firstContactAt: hoursAgo(7),
    lastContactAt: hoursAgo(2),
    createdAt: hoursAgo(7),
    updatedAt: hoursAgo(2),
  },
  {
    id: 'lead-carlos-mendez',
    name: 'Carlos Mendez',
    email: 'carlos.mendez@demo.example',
    phone: '+52-55-5555-1204',
    preferredLanguage: 'es',
    source: 'instagram',
    temperature: 'hot',
    status: 'showing',
    score: 85,
    confidenceLabel: 'high',
    assignedAgentId: 'agent-diego-herrera',
    budget: { min: 3_500_000, max: 5_500_000, currency: 'USD' },
    timeline: '30 days',
    neighborhoods: ['Brickell', 'Downtown Miami'],
    propertyType: 'condo',
    bedroomsMin: 3,
    financingType: 'cash',
    occupation: 'CFO · Mexico City industrial group',
    notes: 'Flying in Friday, wants to see three Brickell options and two pre-con sales galleries.',
    interestedPropertyIds: ['prop-02', 'prop-11', 'prop-12'],
    interestedProjectIds: ['proj-cipriani', 'proj-casa-bella'],
    tags: ['intl', 'cash', 'latam'],
    firstContactAt: hoursAgo(2),
    lastContactAt: minutesAgo(45),
    createdAt: hoursAgo(2),
    updatedAt: minutesAgo(45),
  },

  // ── WARM ──────────────────────────────────────────────────────────────────
  {
    id: 'lead-maria-lopez',
    name: 'Maria Lopez',
    email: 'maria.lopez@demo.example',
    phone: '+1-786-555-1205',
    preferredLanguage: 'es',
    source: 'website',
    temperature: 'warm',
    status: 'qualifying',
    score: 72,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-ana-castillo',
    budget: { min: 1_500_000, max: 2_400_000, currency: 'USD' },
    timeline: '3-6 months',
    neighborhoods: ['Brickell', 'Downtown Miami'],
    propertyType: 'condo',
    bedroomsMin: 2,
    financingType: 'mortgage_70',
    occupation: 'Partner · regional law firm',
    notes: 'Selling Doral home first. Needs lender intro.',
    interestedPropertyIds: ['prop-11', 'prop-12'],
    interestedProjectIds: ['proj-cipriani', 'proj-casa-bella'],
    tags: ['contingent', 'local'],
    firstContactAt: daysAgo(14),
    lastContactAt: daysAgo(1),
    createdAt: daysAgo(14),
    updatedAt: daysAgo(1),
  },
  {
    id: 'lead-david-rodriguez',
    name: 'David Rodriguez',
    email: 'david.rodriguez@demo.example',
    phone: '+1-305-555-1206',
    preferredLanguage: 'en',
    source: 'referral',
    temperature: 'warm',
    status: 'qualified',
    score: 78,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-marco-rivera',
    budget: { min: 5_000_000, max: 7_500_000, currency: 'USD' },
    timeline: '60-90 days',
    neighborhoods: ['Coral Gables', 'Pinecrest'],
    propertyType: 'single_family',
    bedroomsMin: 5,
    financingType: 'mortgage_60',
    occupation: 'Portfolio Manager · Coral Gables fund',
    notes: 'Upsizing for twins. Partner drives final decision.',
    interestedPropertyIds: ['prop-05', 'prop-08'],
    interestedProjectIds: [],
    tags: ['local', 'move-up'],
    demoBrief: DEMO_BRIEF_DAVID_RODRIGUEZ,
    firstContactAt: daysAgo(21),
    lastContactAt: daysAgo(2),
    createdAt: daysAgo(21),
    updatedAt: daysAgo(2),
  },
  {
    id: 'lead-emily-watson',
    name: 'Emily Watson',
    email: 'emily.watson@demo.example',
    phone: '+1-212-555-1207',
    preferredLanguage: 'en',
    source: 'facebook_ads',
    temperature: 'warm',
    status: 'qualifying',
    score: 68,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-natalia-ortiz',
    budget: { min: 2_800_000, max: 4_200_000, currency: 'USD' },
    timeline: '4-6 months',
    neighborhoods: ['Coconut Grove', 'Coral Gables'],
    propertyType: 'single_family',
    bedroomsMin: 4,
    financingType: 'mortgage_75',
    occupation: 'Media executive · NYC',
    notes: 'NYC-based, evaluating Miami as primary. Wants schools info.',
    interestedPropertyIds: ['prop-07', 'prop-05'],
    interestedProjectIds: [],
    tags: ['relo', 'nyc'],
    firstContactAt: daysAgo(18),
    lastContactAt: daysAgo(2),
    createdAt: daysAgo(18),
    updatedAt: daysAgo(2),
  },
  {
    id: 'lead-roberto-silva',
    name: 'Roberto Silva',
    email: 'roberto.silva@demo.example',
    phone: '+55-11-5555-1208',
    preferredLanguage: 'pt',
    source: 'referral',
    temperature: 'warm',
    status: 'qualified',
    score: 74,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-diego-herrera',
    budget: { min: 2_000_000, max: 3_500_000, currency: 'USD' },
    timeline: '3-5 months',
    neighborhoods: ['Brickell', 'Downtown Miami'],
    propertyType: 'condo',
    bedroomsMin: 2,
    financingType: 'cash',
    occupation: 'Founder · São Paulo agribusiness',
    notes: 'Investment + part-time use. Needs rental-friendly building.',
    interestedPropertyIds: ['prop-11', 'prop-12'],
    interestedProjectIds: ['proj-cipriani'],
    tags: ['intl', 'cash', 'latam'],
    demoBrief: DEMO_BRIEF_ROBERTO_SILVA,
    firstContactAt: daysAgo(27),
    lastContactAt: daysAgo(2),
    createdAt: daysAgo(27),
    updatedAt: daysAgo(2),
  },

  // ── NURTURE ───────────────────────────────────────────────────────────────
  {
    id: 'lead-sarah-williams',
    name: 'Sarah Williams',
    email: 'sarah.williams@demo.example',
    phone: '+1-305-555-1209',
    preferredLanguage: 'en',
    source: 'website',
    temperature: 'nurture',
    status: 'nurture',
    score: 52,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-ana-castillo',
    budget: { min: 1_200_000, max: 1_800_000, currency: 'USD' },
    timeline: '9-12 months',
    neighborhoods: ['Coral Gables'],
    propertyType: 'condo',
    bedroomsMin: 2,
    financingType: 'mortgage_75',
    occupation: 'Marketing director',
    notes: 'Watching inventory, not ready to transact.',
    interestedPropertyIds: ['prop-05'],
    interestedProjectIds: ['proj-casa-bella'],
    tags: ['long-horizon'],
    demoBrief: DEMO_BRIEF_SARAH_WILLIAMS,
    firstContactAt: daysAgo(62),
    lastContactAt: daysAgo(5),
    createdAt: daysAgo(62),
    updatedAt: daysAgo(5),
  },
  {
    id: 'lead-michael-park',
    name: 'Michael Park',
    email: 'michael.park@demo.example',
    phone: '+1-646-555-1210',
    preferredLanguage: 'en',
    source: 'zillow',
    temperature: 'nurture',
    status: 'nurture',
    score: 48,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-diego-herrera',
    budget: { min: 900_000, max: 1_400_000, currency: 'USD' },
    timeline: '6-12 months',
    neighborhoods: ['Brickell'],
    propertyType: 'condo',
    bedroomsMin: 1,
    financingType: 'mortgage_80',
    occupation: 'Senior engineer · SaaS',
    notes: 'Exploring remote-work relocation. Budget a stretch for current market.',
    interestedPropertyIds: ['prop-12'],
    interestedProjectIds: ['proj-casa-bella'],
    tags: ['relo', 'tech'],
    firstContactAt: daysAgo(48),
    lastContactAt: daysAgo(6),
    createdAt: daysAgo(48),
    updatedAt: daysAgo(6),
  },
  {
    id: 'lead-ana-garcia',
    name: 'Ana Garcia',
    email: 'ana.garcia@demo.example',
    phone: '+1-305-555-1211',
    preferredLanguage: 'es',
    source: 'instagram',
    temperature: 'nurture',
    status: 'nurture',
    score: 44,
    confidenceLabel: 'low',
    assignedAgentId: 'agent-natalia-ortiz',
    budget: { min: 800_000, max: 1_200_000, currency: 'USD' },
    timeline: '12+ months',
    neighborhoods: ['Aventura', 'Sunny Isles'],
    propertyType: 'condo',
    bedroomsMin: 2,
    financingType: 'mortgage_80',
    occupation: 'Small business owner',
    notes: 'Window-shopping, no pre-approval yet.',
    interestedPropertyIds: [],
    interestedProjectIds: ['proj-mercedes-benz'],
    tags: ['long-horizon', 'needs-lender'],
    firstContactAt: daysAgo(70),
    lastContactAt: daysAgo(7),
    createdAt: daysAgo(70),
    updatedAt: daysAgo(7),
  },
  {
    id: 'lead-thomas-brown',
    name: 'Thomas Brown',
    email: 'thomas.brown@demo.example',
    phone: '+1-617-555-1212',
    preferredLanguage: 'en',
    source: 'referral',
    temperature: 'nurture',
    status: 'nurture',
    score: 55,
    confidenceLabel: 'medium',
    assignedAgentId: 'agent-marco-rivera',
    budget: { min: 3_000_000, max: 4_500_000, currency: 'USD' },
    timeline: '9-12 months',
    neighborhoods: ['Key Biscayne', 'Coconut Grove'],
    propertyType: 'single_family',
    bedroomsMin: 4,
    financingType: 'cash',
    occupation: 'Private equity partner · Boston',
    notes: 'Second home search, waiting for bonus cycle.',
    interestedPropertyIds: ['prop-06', 'prop-07'],
    interestedProjectIds: [],
    tags: ['second-home', 'cash'],
    firstContactAt: daysAgo(55),
    lastContactAt: daysAgo(4),
    createdAt: daysAgo(55),
    updatedAt: daysAgo(4),
  },

  // ── COLD / DEAD ───────────────────────────────────────────────────────────
  {
    id: 'lead-richard-smith',
    name: 'Richard Smith',
    email: 'richard.smith@demo.example',
    phone: '+1-305-555-1213',
    preferredLanguage: 'en',
    source: 'facebook_ads',
    temperature: 'cold',
    status: 'qualifying',
    score: 32,
    confidenceLabel: 'low',
    assignedAgentId: null,
    budget: { min: 400_000, max: 700_000, currency: 'USD' },
    timeline: 'unknown',
    neighborhoods: ['Miami'],
    propertyType: 'condo',
    bedroomsMin: 1,
    financingType: 'unknown',
    occupation: 'unknown',
    notes: 'No response after 3 attempts.',
    interestedPropertyIds: [],
    interestedProjectIds: [],
    tags: ['no-response'],
    firstContactAt: daysAgo(40),
    lastContactAt: daysAgo(30),
    createdAt: daysAgo(40),
    updatedAt: daysAgo(30),
  },
  {
    id: 'lead-jennifer-davis',
    name: 'Jennifer Davis',
    email: 'jennifer.davis@demo.example',
    phone: '+1-305-555-1214',
    preferredLanguage: 'en',
    source: 'website',
    temperature: 'dead',
    status: 'dead',
    score: 28,
    confidenceLabel: 'low',
    assignedAgentId: null,
    budget: { min: 300_000, max: 500_000, currency: 'USD' },
    timeline: 'unknown',
    neighborhoods: ['Miami'],
    propertyType: 'condo',
    bedroomsMin: 1,
    financingType: 'unknown',
    occupation: 'unknown',
    notes: 'Explicitly asked to stop contact.',
    interestedPropertyIds: [],
    interestedProjectIds: [],
    tags: ['do-not-contact'],
    firstContactAt: daysAgo(95),
    lastContactAt: daysAgo(70),
    createdAt: daysAgo(95),
    updatedAt: daysAgo(70),
  },
  {
    id: 'lead-kevin-hughes',
    name: 'Kevin Hughes',
    email: 'kevin.hughes@demo.example',
    phone: '+1-954-555-1215',
    preferredLanguage: 'en',
    source: 'zillow',
    temperature: 'cold',
    status: 'qualifying',
    score: 30,
    confidenceLabel: 'low',
    assignedAgentId: null,
    budget: { min: 600_000, max: 900_000, currency: 'USD' },
    timeline: 'unknown',
    neighborhoods: ['Fort Lauderdale'],
    propertyType: 'condo',
    bedroomsMin: 2,
    financingType: 'unknown',
    occupation: 'unknown',
    notes: 'Out of territory, unresponsive since intake.',
    interestedPropertyIds: [],
    interestedProjectIds: [],
    tags: ['out-of-territory'],
    firstContactAt: daysAgo(36),
    lastContactAt: daysAgo(28),
    createdAt: daysAgo(36),
    updatedAt: daysAgo(28),
  },

  // ── RED FLAG ──────────────────────────────────────────────────────────────
  {
    id: 'lead-alexander-volkov',
    name: 'Alexander Volkov',
    email: 'alex.volkov@demo.example',
    phone: '+1-917-555-1216',
    preferredLanguage: 'en',
    source: 'website',
    temperature: 'red_flag',
    status: 'qualifying',
    score: 42,
    confidenceLabel: 'low',
    assignedAgentId: null,
    budget: { min: 7_000_000, max: 10_000_000, currency: 'USD' },
    timeline: '30 days',
    neighborhoods: ['Miami Beach', 'Fisher Island'],
    propertyType: 'single_family',
    bedroomsMin: 5,
    financingType: 'cash',
    occupation: 'Claims: CEO · undisclosed Moscow holding company',
    notes:
      'Claims $8.5M cash, urgent Moscow relocation. No verifiable company, no public profile. ' +
      'Phone area code (NYC) does not match stated location. Needs manual review.',
    interestedPropertyIds: ['prop-01', 'prop-04'],
    interestedProjectIds: [],
    tags: ['red-flag', 'needs-review', 'sanctions-check'],
    demoBrief: DEMO_BRIEF_VOLKOV,
    firstContactAt: daysAgo(3),
    lastContactAt: hoursAgo(12),
    createdAt: daysAgo(3),
    updatedAt: hoursAgo(12),
  },

  // ── DISQUALIFIED ──────────────────────────────────────────────────────────
  {
    id: 'lead-frank-reed',
    name: 'Frank Reed',
    email: 'frank.reed@demo.example',
    phone: '+1-305-555-1217',
    preferredLanguage: 'en',
    source: 'facebook_ads',
    temperature: 'cold',
    status: 'disqualified',
    score: 18,
    confidenceLabel: 'high',
    assignedAgentId: null,
    budget: { min: 200_000, max: 350_000, currency: 'USD' },
    timeline: 'unknown',
    neighborhoods: ['Miami'],
    propertyType: 'condo',
    bedroomsMin: 1,
    financingType: 'mortgage_95',
    occupation: 'Retail',
    notes: 'Budget and qualification well below team minimum.',
    interestedPropertyIds: [],
    interestedProjectIds: [],
    tags: ['below-minimum'],
    firstContactAt: daysAgo(25),
    lastContactAt: daysAgo(24),
    createdAt: daysAgo(25),
    updatedAt: daysAgo(24),
  },
  {
    id: 'lead-linda-brooks',
    name: 'Linda Brooks',
    email: 'linda.brooks@demo.example',
    phone: '+1-305-555-1218',
    preferredLanguage: 'en',
    source: 'website',
    temperature: 'cold',
    status: 'disqualified',
    score: 22,
    confidenceLabel: 'high',
    assignedAgentId: null,
    budget: { min: 250_000, max: 400_000, currency: 'USD' },
    timeline: 'unknown',
    neighborhoods: ['Miami'],
    propertyType: 'condo',
    bedroomsMin: 1,
    financingType: 'mortgage_97',
    occupation: 'Hospitality',
    notes: 'Out of service area and below minimum price point.',
    interestedPropertyIds: [],
    interestedProjectIds: [],
    tags: ['below-minimum'],
    firstContactAt: daysAgo(33),
    lastContactAt: daysAgo(32),
    createdAt: daysAgo(33),
    updatedAt: daysAgo(32),
  },
];

export const leadById = Object.fromEntries(leads.map((l) => [l.id, l]));
